import type {
  DashboardParameter,
  DashboardParameterValue,
  DashboardWidget,
} from "../types";

/**
 * Shared parameter helpers used by both the live `DashboardDetailView`
 * and the `DashboardCreateView` preview pane.
 *
 * Centralizing this prevents the preview from rendering broken widgets
 * because it forgot to expand `:date_range` into `:date_range_start` /
 * `:date_range_end`, which is exactly the bug we're fixing here. The
 * helpers are pure so they're easy to unit-test alongside the views
 * (or just visually round-trip them through both surfaces).
 */

/**
 * Resolve a single parameter to its initial value.
 *
 * The Pydantic model on the server stores omitted defaults as `null`,
 * not `undefined`, so we treat both as "no explicit default" and fall
 * back to a sensible starting value:
 *   - `date_range` → `{ preset: "last_90_days" }`
 *   - `boolean`    → `false`
 *   - everything else → `null`
 */
export function defaultParameterValue(parameter: DashboardParameter): DashboardParameterValue {
  if (parameter.default !== undefined && parameter.default !== null) {
    return parameter.default;
  }
  if (parameter.type === "date_range") return { preset: "last_90_days" };
  if (parameter.type === "boolean") return false;
  if (parameter.type === "text" || parameter.type === "select") return null;
  return null;
}

/**
 * Initialize a `parameterValues` map from the dashboard's declared
 * parameters. Used as the seed state for the live view and the
 * starting point for the create-view preview.
 */
export function initializeParameterValues(
  parameters: DashboardParameter[] | undefined,
): Record<string, DashboardParameterValue> {
  const next: Record<string, DashboardParameterValue> = {};
  for (const parameter of parameters ?? []) {
    next[parameter.id] = defaultParameterValue(parameter);
  }
  return next;
}

/**
 * Stable signature for the date_range slice of `values`.
 *
 * Used by the live view to detect when the user actually touched a
 * date filter (vs. firing an unrelated row-click drilldown), so the
 * "now" anchor only re-rolls when needed and unrelated widgets don't
 * bust their cache keys.
 */
export function dateRangeSignature(
  values: Record<string, DashboardParameterValue>,
): string {
  const entries = Object.entries(values).filter(
    ([, value]) => value !== null && typeof value === "object" && "preset" in value,
  );
  entries.sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
}

/**
 * Expand `date_range` parameters into `_start` / `_end` ISO timestamp
 * pairs, anchored at `now`. Drops the raw `{ preset }` object from the
 * resolved map so widgets that bind `:date_range` directly don't ship
 * a Python dict that asyncpg can't serialize as a SQL parameter.
 */
export function expandParams(
  values: Record<string, DashboardParameterValue>,
  now: Date,
): Record<string, DashboardParameterValue> {
  const expanded: Record<string, DashboardParameterValue> = { ...values };
  for (const [id, value] of Object.entries(values)) {
    if (!value || typeof value !== "object") continue;

    // Custom date range — `{ start: ISO, end: ISO }`. Pass through as
    // already-resolved ISO timestamps; the server's
    // `_coerce_param_value` will turn them into `datetime` instances
    // for asyncpg's binary protocol.
    if ("start" in value && "end" in value) {
      const start = (value as { start: unknown }).start;
      const end = (value as { end: unknown }).end;
      if (typeof start === "string" && typeof end === "string") {
        delete expanded[id];
        expanded[`${id}_start`] = ensureIsoTimestamp(start, false);
        expanded[`${id}_end`] = ensureIsoTimestamp(end, true);
        continue;
      }
    }

    if (!("preset" in value)) continue;
    const preset = value.preset ?? "last_90_days";
    delete expanded[id];
    if (preset === "all") {
      // Use a full ISO timestamp (with "T") so the server's
      // _coerce_param_value coerces it to a `datetime`. asyncpg rejects
      // bare YYYY-MM-DD strings for `timestamptz` parameters.
      expanded[`${id}_start`] = new Date(0).toISOString();
      expanded[`${id}_end`] = now.toISOString();
      continue;
    }
    const days = preset === "last_7_days" ? 7 : preset === "last_30_days" ? 30 : 90;
    const start = new Date(now);
    start.setDate(start.getDate() - days);
    expanded[`${id}_start`] = start.toISOString();
    expanded[`${id}_end`] = now.toISOString();
  }
  return expanded;
}

/**
 * Normalize a user-supplied date string to an ISO timestamp the server
 * can coerce. Date inputs hand us a bare ``YYYY-MM-DD`` string, which
 * asyncpg rejects when binding to `timestamptz`. We anchor to the
 * start of day for `_start` and end of day for `_end` so a custom
 * range like "2026-01-15 to 2026-01-15" actually covers the full day.
 */
function ensureIsoTimestamp(value: string, isEnd: boolean): string {
  // Already a full ISO timestamp — pass through.
  if (value.includes("T")) return value;
  // Bare YYYY-MM-DD — anchor to start or end of day in UTC.
  const time = isEnd ? "T23:59:59.999Z" : "T00:00:00.000Z";
  return `${value}${time}`;
}

/** True when `value` is something we'd consider a "set" parameter. */
export function parameterHasValue(value: DashboardParameterValue | undefined): boolean {
  return value !== undefined && value !== null && value !== "";
}

/**
 * True when ``params`` carries a usable value for ``paramId``, accounting
 * for the date_range expansion that swaps a single ``date_range`` key for
 * a ``_start`` / ``_end`` pair (so widgets requiring ``date_range`` are
 * still satisfied once the values have been expanded).
 */
export function hasResolvedParam(
  paramId: string,
  params: Record<string, DashboardParameterValue> | undefined,
): boolean {
  if (!params) return false;
  if (parameterHasValue(params[paramId])) return true;
  return (
    parameterHasValue(params[`${paramId}_start`]) &&
    parameterHasValue(params[`${paramId}_end`])
  );
}

/**
 * Return true when ``widget`` should be visible given the current
 * parameter values. A widget is hidden when any of its declared
 * ``requires`` parameters is unset — that's the canonical "don't render
 * the drill-down section until the user picks something" gate.
 *
 * Centralized so both the live ``DashboardDetailView`` and the
 * ``DashboardCreateView`` preview hide the same widgets at the same
 * time. Otherwise the preview and the saved view would disagree about
 * what's visible — exactly the bug a power user would catch first.
 */
export function isWidgetVisible(
  widget: DashboardWidget,
  params: Record<string, DashboardParameterValue> | undefined,
): boolean {
  const requires = widget.requires ?? [];
  if (requires.length === 0) return true;
  return requires.every((paramId) => hasResolvedParam(paramId, params));
}

/**
 * Compute which widgets transitioned from hidden to visible between
 * two parameter snapshots. Used by the views to scroll a newly-
 * revealed drill-down section into view immediately after a row click,
 * so the just-loaded content doesn't land below the fold.
 */
export function newlyVisibleWidgetIds(
  widgets: DashboardWidget[],
  prev: Record<string, DashboardParameterValue> | undefined,
  next: Record<string, DashboardParameterValue> | undefined,
): string[] {
  const out: string[] = [];
  for (const widget of widgets) {
    if (!isWidgetVisible(widget, prev) && isWidgetVisible(widget, next)) {
      out.push(widget.id);
    }
  }
  return out;
}

/**
 * Serialize parameter values into URL search-params so a drill-down
 * survives a page refresh and can be deep-linked / pasted into Slack.
 *
 * Encoding scheme:
 *   - `date_range` parameters serialize either as `<id>=preset:last_30_days`
 *     or `<id>=range:<startISO>..<endISO>` for custom windows.
 *   - `boolean` parameters serialize as `<id>=true|false`.
 *   - Everything else stringifies as-is (UUIDs, free-text, numbers).
 *   - Parameters whose values match the dashboard's defaults are
 *     OMITTED from the URL — keeps the link short and avoids stamping
 *     "default" values in stone (so a future dashboard edit that
 *     changes the default doesn't get overridden by a stale URL).
 */
export function encodeParameterValues(
  values: Record<string, DashboardParameterValue>,
  parameters: DashboardParameter[] | undefined,
): URLSearchParams {
  const params = new URLSearchParams();
  const defaults = initializeParameterValues(parameters);
  for (const [id, value] of Object.entries(values)) {
    if (!parameterHasValue(value)) continue;
    const defaultValue = defaults[id];
    if (parameterValuesEqual(value, defaultValue)) continue;

    const definition = parameters?.find((p) => p.id === id);
    if (definition?.type === "date_range") {
      const encoded = encodeDateRangeValue(value);
      if (encoded) params.set(id, encoded);
      continue;
    }
    if (definition?.type === "select" || definition?.type === "text") {
      if (typeof value === "string") {
        params.set(id, value);
      }
      continue;
    }
    if (typeof value === "boolean") {
      params.set(id, value ? "true" : "false");
      continue;
    }
    if (typeof value === "number") {
      params.set(id, String(value));
      continue;
    }
    if (typeof value === "string") {
      params.set(id, value);
      continue;
    }
    // Fallback — JSON-encode anything else (rare).
    params.set(id, JSON.stringify(value));
  }
  return params;
}

/**
 * Inverse of ``encodeParameterValues``. Given the dashboard's parameter
 * declarations and the current URL search-params, hydrate the values
 * map. Falls back to the defaults for any parameter that wasn't
 * present in the URL or that decoded as garbage (defensive — a
 * malformed URL shouldn't crash the dashboard).
 */
export function decodeParameterValues(
  search: URLSearchParams,
  parameters: DashboardParameter[] | undefined,
): Record<string, DashboardParameterValue> {
  const out = initializeParameterValues(parameters);
  for (const parameter of parameters ?? []) {
    if (!search.has(parameter.id)) continue;
    const raw = search.get(parameter.id);
    if (raw === null) continue;
    try {
      if (parameter.type === "date_range") {
        const decoded = decodeDateRangeValue(raw);
        if (decoded !== null) out[parameter.id] = decoded;
        continue;
      }
      if (parameter.type === "boolean") {
        out[parameter.id] = raw === "true";
        continue;
      }
      if (parameter.type === "number") {
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) out[parameter.id] = parsed;
        continue;
      }
      if (parameter.type === "select" || parameter.type === "text") {
        out[parameter.id] = raw;
        continue;
      }
      // string + everything else → raw text.
      out[parameter.id] = raw;
    } catch {
      // Garbage input — leave the default value in place rather than
      // exploding the page.
    }
  }
  return out;
}

/**
 * `date_range` parameters now support EITHER a preset (`last_30_days`)
 * OR a concrete window (`{ start: ISO, end: ISO }`). The runtime
 * `expandParams` already special-cases both shapes; this helper
 * normalizes them for URL encoding.
 */
function encodeDateRangeValue(value: DashboardParameterValue): string | null {
  if (value === null || value === undefined || typeof value !== "object") return null;
  if ("preset" in value && typeof value.preset === "string") {
    return `preset:${value.preset}`;
  }
  if (
    "start" in value &&
    "end" in value &&
    typeof (value as Record<string, unknown>).start === "string" &&
    typeof (value as Record<string, unknown>).end === "string"
  ) {
    const start = (value as { start: string }).start;
    const end = (value as { end: string }).end;
    return `range:${start}..${end}`;
  }
  return null;
}

function decodeDateRangeValue(raw: string): DashboardParameterValue | null {
  if (raw.startsWith("preset:")) {
    const preset = raw.slice("preset:".length);
    if (!preset) return null;
    return { preset } as DashboardParameterValue;
  }
  if (raw.startsWith("range:")) {
    const body = raw.slice("range:".length);
    const sep = body.indexOf("..");
    if (sep <= 0) return null;
    const start = body.slice(0, sep);
    const end = body.slice(sep + 2);
    if (!start || !end) return null;
    return { start, end } as DashboardParameterValue;
  }
  return null;
}

function parameterValuesEqual(
  a: DashboardParameterValue | undefined,
  b: DashboardParameterValue | undefined,
): boolean {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) {
    return a === b;
  }
  if (typeof a === "object" || typeof b === "object") {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return String(a) === String(b);
}
